"use client";

import {
	MutationCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { useMemo } from "react";
import { Toaster, toast } from "sonner";

import { AppConfigProvider } from "@/lib/config";

import type { AppConfig } from "@/lib/config-server";
import type { ReactNode } from "react";

interface ProvidersProps {
	children: ReactNode;
	config: AppConfig;
}

function extractErrorMessage(error: unknown): string {
	// openapi-fetch throws the parsed JSON response body directly
	// (not an Error instance) for non-OK responses.
	const err = error as Record<string, unknown> | undefined;
	return (
		(typeof err?.message === "string" && err.message) ||
		(error instanceof Error ? error.message : "An unknown error occurred.")
	);
}

export function Providers({ children, config }: ProvidersProps) {
	const queryClient = useMemo(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						staleTime: 30 * 1000,
						retry: false,
					},
				},
				mutationCache: new MutationCache({
					onError: (error) => {
						toast.error(extractErrorMessage(error));
					},
				}),
			}),
		[],
	);

	return (
		<AppConfigProvider config={config}>
			<ThemeProvider
				attribute="class"
				defaultTheme="system"
				enableSystem
				storageKey="theme"
			>
				<QueryClientProvider client={queryClient}>
					{children}
				</QueryClientProvider>
				<Toaster />
			</ThemeProvider>
		</AppConfigProvider>
	);
}
